import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import client from '../api/client.js'
import * as kycDocsApi from '../api/kycDocuments.js'

export const useKycStore = defineStore('kyc', () => {
  const individualKyc = ref(null)
  const legalProfile = ref(null)
  const kycStatus = ref(null)
  const kycDocuments = ref([])
  const loading = ref(false)
  const uploadError = ref(null)

  // ── Individual KYC ──────────────────────────────────────────────────────────

  async function fetchIndividualKyc(groupId) {
    loading.value = true
    try {
      const { data } = await client.get('/api/kyc/individual', { params: { groupId } })
      individualKyc.value = data
      return data
    } finally {
      loading.value = false
    }
  }

  async function submitIndividualKyc(payload) {
    loading.value = true
    try {
      const { data } = await client.post('/api/kyc/individual', payload)
      individualKyc.value = data
      return data
    } finally {
      loading.value = false
    }
  }

  // ── Organization KYC ────────────────────────────────────────────────────────

  async function fetchLegalProfile(groupId) {
    loading.value = true
    try {
      const { data } = await client.get('/api/kyc/organization', { params: { groupId } })
      legalProfile.value = data
      return data
    } finally {
      loading.value = false
    }
  }

  async function submitOrganizationKyc(payload) {
    loading.value = true
    try {
      const { data } = await client.post('/api/kyc/organization', payload)
      legalProfile.value = data
      return data
    } finally {
      loading.value = false
    }
  }

  // ── Document CRUD ───────────────────────────────────────────────────────────

  async function attachDocument(payload) {
    loading.value = true
    uploadError.value = null
    try {
      const { data } = await client.post('/api/kyc/documents', payload)
      kycDocuments.value.unshift(data)
      return data
    } finally {
      loading.value = false
    }
  }

  async function fetchDocuments(groupId) {
    uploadError.value = null
    try {
      const docs = await kycDocsApi.fetchKycDocuments(groupId)
      kycDocuments.value = docs
      return docs
    } catch (err) {
      uploadError.value = err?.message
      return []
    }
  }

  /**
   * Run the full 3-step direct-to-R2 upload flow and return the saved document.
   */
  async function uploadKycDocument({ groupId, documentType, issuingCountry, file, frontSide = false }) {
    loading.value = true
    uploadError.value = null
    try {
      const doc = await kycDocsApi.uploadFile(groupId, documentType, issuingCountry, file, frontSide)
      kycDocuments.value = [...kycDocuments.value, doc]
      return doc
    } finally {
      loading.value = false
    }
  }

  async function confirmKycDocument(groupId, sessionId, payload) {
    loading.value = true
    uploadError.value = null
    try {
      const doc = await kycDocsApi.confirmKycDocumentUpload(groupId, sessionId, payload)
      const idx = kycDocuments.value.findIndex(d => d.id === doc.id)
      if (idx >= 0) kycDocuments.value[idx] = doc
      else kycDocuments.value.push(doc)
      return doc
    } finally {
      loading.value = false
    }
  }

  async function getDocumentViewUrl(groupId, documentId) {
    const { data } = await kycDocsApi.getDocumentViewUrl(groupId, documentId)
    return data
  }

  // ── Status ──────────────────────────────────────────────────────────────────

  async function fetchKycStatus(groupId) {
    loading.value = true
    try {
      const { data } = await client.get(`/api/kyc/status/${groupId}`)
      kycStatus.value = data
      if (data.individualKyc) individualKyc.value = data.individualKyc
      if (data.legalProfile) legalProfile.value = data.legalProfile
      if (data.documents) kycDocuments.value = data.documents
      return data
    } finally {
      loading.value = false
    }
  }

  // ── Admin: verify / reject ──────────────────────────────────────────────────

  async function verifyKyc(groupId) {
    loading.value = true
    try {
      await client.post(`/api/kyc/${groupId}/verify`)
      if (kycStatus.value) kycStatus.value.status = 'VERIFIED'
      if (individualKyc.value) individualKyc.value.status = 'VERIFIED'
      if (legalProfile.value) legalProfile.value.verificationStatus = 'VERIFIED'
    } finally {
      loading.value = false
    }
  }

  async function rejectKyc(groupId, reason) {
    loading.value = true
    try {
      await client.post(`/api/kyc/${groupId}/reject`, { reason })
      if (kycStatus.value) {
        kycStatus.value.status = 'REJECTED'
        kycStatus.value.rejectionReason = reason
      }
    } finally {
      loading.value = false
    }
  }

  // ── Computed helpers ────────────────────────────────────────────────────────

  const statusLabel = computed(() => {
    const s = kycStatus.value?.status
    if (!s) return '—'
    return s.charAt(0) + s.slice(1).toLowerCase()
  })

  const isPending = computed(() => kycStatus.value?.status === 'PENDING')
  const isInProgress = computed(() => kycStatus.value?.status === 'IN_PROGRESS')
  const isVerified = computed(() => kycStatus.value?.status === 'VERIFIED')
  const isRejected = computed(() => kycStatus.value?.status === 'REJECTED' || kycStatus.value?.status === 'FAILED')

  const hasUploadError = computed(() => !!uploadError.value)

  return {
    individualKyc, legalProfile, kycStatus, kycDocuments, loading, uploadError,
    fetchIndividualKyc, submitIndividualKyc,
    fetchLegalProfile, submitOrganizationKyc,
    attachDocument, fetchDocuments, uploadKycDocument, confirmKycDocument, getDocumentViewUrl,
    fetchKycStatus, verifyKyc, rejectKyc,
    statusLabel, isPending, isInProgress, isVerified, isRejected, hasUploadError,
  }
})